import React, { useEffect, useMemo, useState } from 'react';
import styles from './AdminShared.module.scss';
import { EmailLogDto, PagedResult } from '../../../types/admin.types';
import { fetchEmailLogsPaged } from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';

const pageSize = 25;
const emptyList: PagedResult<EmailLogDto> = {
    Results: [],
    PageIndex: 1,
    PageSize: pageSize,
    TotalPages: 0,
    TotalCount: 0
};

const formatDateTime = (value?: string | null) => {
    if (!value || value.trim().length === 0) {
        return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleString();
};

const formatSentDateTime = (value?: string | null) => {
    if (!value || value.trim().length === 0) {
        return 'Pending';
    }

    return formatDateTime(value);
};

const AdminEmailLogs = () => {
    const { getUserInfo } = useUserState();
    const token = getUserInfo()?.token;
    const [listData, setListData] = useState<PagedResult<EmailLogDto>>(emptyList);
    const [selected, setSelected] = useState<EmailLogDto | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [searchInput, setSearchInput] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [pageIndex, setPageIndex] = useState(1);

    const loadItems = async (page: number, query: string) => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const response = await fetchEmailLogsPaged(page, pageSize, query, token);
            setListData(response);
            if (response.Results.length > 0) {
                const first = response.Results[0];
                setSelected((current) => {
                    if (!current) {
                        return first;
                    }

                    return response.Results.find((item) =>
                        item.EmailAddress === current.EmailAddress &&
                        item.CreatedDate === current.CreatedDate &&
                        item.Message === current.Message
                    ) ?? first;
                });
            } else {
                setSelected(null);
            }
        } catch (error) {
            setErrorMessage('Unable to load email logs.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadItems(pageIndex, appliedSearch);
    }, [pageIndex, appliedSearch]);

    const selectedName = useMemo(
        () => selected ? `${selected.FirstName} ${selected.LastName ?? ''}`.trim() : '',
        [selected]
    );

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Email Logs</h1>
                    <p>Review recent contact-us messages.</p>
                </div>
            </header>

            {errorMessage ? <div className={styles.muted}>{errorMessage}</div> : null}

            <div className={styles.content}>
                <section className={styles.panel}>
                    <h2>Messages</h2>
                    <div className={styles.actions}>
                        <input
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            placeholder="Search by name or email"
                            maxLength={200}
                        />
                        <button
                            type="button"
                            className={styles.secondary}
                            onClick={() => {
                                setPageIndex(1);
                                setAppliedSearch(searchInput);
                            }}
                        >
                            Search
                        </button>
                        <button
                            type="button"
                            className={styles.secondary}
                            onClick={() => {
                                setSearchInput('');
                                setPageIndex(1);
                                setAppliedSearch('');
                            }}
                        >
                            Clear
                        </button>
                    </div>

                    {isLoading ? (
                        <div className={styles.muted}>Loading emails...</div>
                    ) : listData.Results.length === 0 ? (
                        <div className={styles.empty}>No emails recorded.</div>
                    ) : (
                        <>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Created</th>
                                        <th>Sent</th>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {listData.Results.map((item, index) => (
                                        <tr key={`${item.EmailAddress}-${item.CreatedDate ?? index}`}>
                                            <td>{formatDateTime(item.CreatedDate)}</td>
                                            <td>{formatSentDateTime(item.SentDateTime)}</td>
                                            <td>{`${item.FirstName} ${item.LastName ?? ''}`.trim()}</td>
                                            <td>{item.EmailAddress}</td>
                                            <td>
                                                <button type="button" className={styles.secondary} onClick={() => setSelected(item)}>
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <div className={styles.actions}>
                                <button
                                    type="button"
                                    className={styles.secondary}
                                    disabled={listData.PageIndex <= 1}
                                    onClick={() => setPageIndex((prev) => Math.max(1, prev - 1))}
                                >
                                    Prev
                                </button>
                                <span className={styles.muted}>
                                    Page {listData.PageIndex} of {Math.max(1, listData.TotalPages)}
                                </span>
                                <button
                                    type="button"
                                    className={styles.secondary}
                                    disabled={listData.PageIndex >= listData.TotalPages}
                                    onClick={() => setPageIndex((prev) => prev + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </>
                    )}
                </section>

                <section className={styles.panel}>
                    <h2>Message Details</h2>
                    {selected ? (
                        <div>
                            <div className={styles.field}>
                                <label>Name</label>
                                <div>{selectedName}</div>
                            </div>
                            <div className={styles.field}>
                                <label>Email</label>
                                <div>{selected.EmailAddress}</div>
                            </div>
                            <div className={styles.field}>
                                <label>Created</label>
                                <div>{formatDateTime(selected.CreatedDate)}</div>
                            </div>
                            <div className={styles.field}>
                                <label>Sent</label>
                                <div>{formatSentDateTime(selected.SentDateTime)}</div>
                            </div>
                            <div className={styles.field}>
                                <label>Message</label>
                                <div className={styles.readOnlyBlock}>{selected.Message}</div>
                            </div>
                        </div>
                    ) : (
                        <div className={styles.muted}>Select an email to view details.</div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default AdminEmailLogs;
